// Role-based access control configuration
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  MINISTER: 'minister',
  /** Legacy full-admin role (still in DB `users.role`); treat like directorate admin for RBAC. */
  ADMIN: 'admin',
  ADMIN_DIRECTORATE: 'admin_directorate',
  ADMIN_SECRETARIAT: 'admin_secretariat',
  COMMISSIONER: 'commissioner',
  ADDITIONAL_SECRETARY: 'additional_secretary',
  JOINT_SECRETARY: 'joint_secretary',
  DEPUTY_SECRETARY: 'deputy_secretary',
  UNDER_SECRETARY: 'under_secretary',
  JOINT_DIRECTOR: 'joint_director',
  DEPUTY_DIRECTOR: 'deputy_director',
  SUPERINTENDENT: 'superintendent',
  ASSISTANT: 'assistant',
  SECRETARIAT: 'secretariat',
  DEO: 'deo',
  SDEO: 'sdeo',
  HOI: 'hoi',
  TEACHER: 'teacher'
} as const

type CoreRole = (typeof ROLES)[keyof typeof ROLES]
export type UserRole = CoreRole

export const BRANCH_ADMIN_ROLES = [
  ROLES.ADMIN_DIRECTORATE,
  ROLES.ADMIN_SECRETARIAT,
  ROLES.COMMISSIONER,
  ROLES.ADDITIONAL_SECRETARY,
  ROLES.JOINT_SECRETARY,
  ROLES.DEPUTY_SECRETARY,
  ROLES.UNDER_SECRETARY,
  ROLES.JOINT_DIRECTOR,
  ROLES.DEPUTY_DIRECTOR,
  ROLES.SUPERINTENDENT,
  ROLES.ASSISTANT
] as const
export type BranchAdminRole = typeof BRANCH_ADMIN_ROLES[number]

/** Roles that see only Directorate Path transfer reports */
export const DIRECTORATE_PATH_ROLES = [
  ROLES.ADMIN_DIRECTORATE,
  ROLES.ASSISTANT,
  ROLES.SUPERINTENDENT,
  ROLES.DEPUTY_DIRECTOR,
  ROLES.JOINT_DIRECTOR
] as const

/** Roles that see only Secretariat Path transfer reports */
export const SECRETARIAT_PATH_ROLES = [
  ROLES.ADMIN_SECRETARIAT,
  ROLES.UNDER_SECRETARY,
  ROLES.DEPUTY_SECRETARY,
  ROLES.JOINT_SECRETARY,
  ROLES.ADDITIONAL_SECRETARY,
  ROLES.COMMISSIONER
] as const

export const ADMIN_FAMILY_ROLES = [ROLES.SUPER_ADMIN, ...BRANCH_ADMIN_ROLES] as const
export const ADMIN_ACCESS_ROLES = ADMIN_FAMILY_ROLES

const BRANCH_ADMIN_ROLE_SET = new Set<string>(BRANCH_ADMIN_ROLES as readonly string[])
const DIRECTORATE_PATH_ROLE_SET = new Set<string>(DIRECTORATE_PATH_ROLES as readonly string[])
const SECRETARIAT_PATH_ROLE_SET = new Set<string>(SECRETARIAT_PATH_ROLES as readonly string[])
const ADMIN_FAMILY_ROLE_SET = new Set<string>(ADMIN_FAMILY_ROLES as readonly string[])

export const isBranchAdminRole = (role?: string | null): role is BranchAdminRole => {
  if (!role) return false
  return BRANCH_ADMIN_ROLE_SET.has(role.toLowerCase())
}

export const isDirectoratePathRole = (role?: string | null): boolean => {
  if (!role) return false
  return DIRECTORATE_PATH_ROLE_SET.has(role.toLowerCase())
}

export const isSecretariatPathRole = (role?: string | null): boolean => {
  if (!role) return false
  return SECRETARIAT_PATH_ROLE_SET.has(role.toLowerCase())
}

export const isAdminFamilyRole = (role?: string | null) => {
  if (!role) return false
  const lower = role.toLowerCase()
  if (lower === ROLES.ADMIN) return true
  return ADMIN_FAMILY_ROLE_SET.has(lower)
}

/** Matches server educationQualificationPolicy — who can verify / edit locked education rows */
const EDUCATION_QUALIFICATION_APPROVER_SET = new Set<string>([
  ROLES.SUPER_ADMIN,
  ROLES.MINISTER,
  ROLES.ADMIN_DIRECTORATE,
  ROLES.ADMIN_SECRETARIAT,
  ROLES.SECRETARIAT,
  ROLES.HOI,
  ROLES.SDEO,
  ROLES.DEO,
  ...BRANCH_ADMIN_ROLES,
])

export const canApproveEducationQualificationRole = (role?: string | null): boolean => {
  if (!role) return false
  return EDUCATION_QUALIFICATION_APPROVER_SET.has(role.toLowerCase())
}

export const normalizeAdminRole = (role?: string | null): UserRole | '' => {
  if (!role) return ''
  const lower = role.toLowerCase()
  if (isBranchAdminRole(lower)) {
    return lower
  }
  if (lower === ROLES.SUPER_ADMIN) {
    return ROLES.SUPER_ADMIN
  }
  return lower as UserRole
}

// Role hierarchy (higher number = more permissions)
export const ROLE_HIERARCHY = {
  super_admin: 15,
  minister: 14,
  admin: 12,
  admin_directorate: 12,
  admin_secretariat: 12,
  commissioner: 11,
  additional_secretary: 10,
  joint_secretary: 9,
  deputy_secretary: 8,
  under_secretary: 7,
  joint_director: 7,
  deputy_director: 6,
  superintendent: 5,
  assistant: 4,
  secretariat: 4,
  deo: 3,
  sdeo: 2,
  hoi: 1,
  teacher: 0
} as const satisfies Record<UserRole, number>

// Permissions for each role
const SUPER_ADMIN_PERMISSIONS = [
  'users.create',
  'users.read',
  'users.update',
  'users.delete',
  'users.manage_roles',
  'schools.create',
  'schools.read',
  'schools.update',
  'schools.delete',
  'schools.export',
  'teachers.create',
  'teachers.read',
  'teachers.update',
  'teachers.delete',
  'teachers.export',
  'medical_records.create',
  'medical_records.read',
  'medical_records.update',
  'medical_records.delete',
  'master_data.create',
  'master_data.read',
  'master_data.update',
  'master_data.delete',
  'master_data.manage_roles',
  'analytics.read'
] as const

const MINISTER_PERMISSIONS = [
  'schools.read',
  'schools.export',
  'teachers.read',
  'teachers.export',
  'medical_records.read',
  'master_data.read',
  'analytics.read',
  'policy.read',
  'policy.update',
  'budget.read',
  'budget.export',
  'performance.read',
  'performance.export'
] as const

const ADMIN_PERMISSIONS = [
  'schools.create',
  'schools.read',
  'schools.update',
  'schools.delete',
  'schools.export',
  'teachers.create',
  'teachers.read',
  'teachers.update',
  'teachers.delete',
  'teachers.export',
  'medical_records.create',
  'medical_records.read',
  'medical_records.update',
  'medical_records.delete',
  'master_data.create',
  'master_data.read',
  'master_data.update',
  'master_data.delete',
  'analytics.read',
  'joining_reports.read',
  'joining_reports.verify'
] as const

const BRANCH_APPROVER_PERMISSIONS = [
  'schools.read',
  'schools.export',
  'teachers.read',
  'teachers.export',
  'medical_records.read',
  'master_data.read',
  'analytics.read',
  'joining_reports.read',
  'joining_reports.verify'
] as const

export const ROLE_PERMISSIONS: Record<UserRole, readonly string[]> = {
  [ROLES.SUPER_ADMIN]: SUPER_ADMIN_PERMISSIONS,
  [ROLES.MINISTER]: MINISTER_PERMISSIONS,
  [ROLES.ADMIN]: ADMIN_PERMISSIONS,
  [ROLES.ADMIN_DIRECTORATE]: ADMIN_PERMISSIONS,
  [ROLES.ADMIN_SECRETARIAT]: ADMIN_PERMISSIONS,
  [ROLES.COMMISSIONER]: BRANCH_APPROVER_PERMISSIONS,
  [ROLES.ADDITIONAL_SECRETARY]: BRANCH_APPROVER_PERMISSIONS,
  [ROLES.JOINT_SECRETARY]: BRANCH_APPROVER_PERMISSIONS,
  [ROLES.DEPUTY_SECRETARY]: BRANCH_APPROVER_PERMISSIONS,
  [ROLES.UNDER_SECRETARY]: BRANCH_APPROVER_PERMISSIONS,
  [ROLES.JOINT_DIRECTOR]: BRANCH_APPROVER_PERMISSIONS,
  [ROLES.DEPUTY_DIRECTOR]: BRANCH_APPROVER_PERMISSIONS,
  [ROLES.SUPERINTENDENT]: BRANCH_APPROVER_PERMISSIONS,
  [ROLES.ASSISTANT]: BRANCH_APPROVER_PERMISSIONS,
  [ROLES.SECRETARIAT]: BRANCH_APPROVER_PERMISSIONS,
  [ROLES.DEO]: [
    // School Management (District Level)
    'schools.read',
    'schools.update',
    'schools.export',
    
    // Teacher Management (District Level)
    'teachers.read',
    'teachers.update',
    'teachers.export',
    
    // Medical Records (View Only)
    'medical_records.read',
    
    // Master Data Management (View Only)
    'master_data.read',
    
    // Analytics (District Level)
    'analytics.read',
    
    // Joining Reports
    'joining_reports.read',
    'joining_reports.verify'
  ],
  [ROLES.SDEO]: [
    // School Management (Sub-Division Level)
    'schools.read',
    'schools.update',
    'schools.export',
    
    // Teacher Management (Sub-Division Level)
    'teachers.read',
    'teachers.update',
    'teachers.export',
    
    // Medical Records (View Only)
    'medical_records.read',
    
    // Master Data Management (View Only)
    'master_data.read',
    
    // Analytics (Sub-Division Level)
    'analytics.read',
    
    // Joining Reports
    'joining_reports.read',
    'joining_reports.verify'
  ],
  [ROLES.HOI]: [
    // School Management (Institution Level)
    'schools.read',
    'schools.update',
    'schools.export',
    
    // Teacher Management (Institution Level)
    'teachers.read',
    'teachers.export',
    
    // Medical Records (View Only)
    'medical_records.read',
    
    // Master Data Management (View Only)
    'master_data.read',
    
    // Analytics (Institution Level)
    'analytics.read',
    
    // Joining Reports
    'joining_reports.create',
    'joining_reports.read_own',
    'joining_reports.delete_own'
  ],
  [ROLES.TEACHER]: [
    // Limited access
    'teachers.read_own',
    'medical_records.read',
    'medical_records.read_own',
    'medical_records.submit_own',
    'profile.update_own'
  ]
}

// Helper functions
export const hasRole = (userRole: UserRole, requiredRole: UserRole): boolean => {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole]
}

export const hasPermission = (userRole: UserRole, permission: string): boolean => {
  const rolePermissions = ROLE_PERMISSIONS[userRole] || []
  return rolePermissions.includes(permission as any)
}

export const canAccess = (userRole: UserRole, requiredRole: UserRole, permission?: string): boolean => {
  if (!hasRole(userRole, requiredRole)) {
    return false
  }
  
  if (permission && !hasPermission(userRole, permission)) {
    return false
  }
  
  return true
}

// Role display names
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  [ROLES.SUPER_ADMIN]: 'Super Administrator',
  [ROLES.MINISTER]: 'Education Minister',
  [ROLES.ADMIN]: 'Administrator (Legacy)',
  [ROLES.ADMIN_DIRECTORATE]: 'Administrator (Directorate)',
  [ROLES.ADMIN_SECRETARIAT]: 'Administrator (Secretariat)',
  [ROLES.COMMISSIONER]: 'Commissioner',
  [ROLES.ADDITIONAL_SECRETARY]: 'Additional Secretary',
  [ROLES.JOINT_SECRETARY]: 'Joint Secretary',
  [ROLES.DEPUTY_SECRETARY]: 'Deputy Secretary',
  [ROLES.UNDER_SECRETARY]: 'Under Secretary',
  [ROLES.JOINT_DIRECTOR]: 'Joint Director',
  [ROLES.DEPUTY_DIRECTOR]: 'Deputy Director',
  [ROLES.SUPERINTENDENT]: 'Superintendent',
  [ROLES.ASSISTANT]: 'Assistant',
  [ROLES.SECRETARIAT]: 'Secretariat',
  [ROLES.DEO]: 'District Education Officer',
  [ROLES.SDEO]: 'Sub-Division Education Officer',
  [ROLES.HOI]: 'Head of Institution',
  [ROLES.TEACHER]: 'Teacher'
}

// Role descriptions
export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [ROLES.SUPER_ADMIN]: 'Full system access including user management and role assignment',
  [ROLES.MINISTER]: 'High-level access to view system-wide analytics and policy management',
  [ROLES.ADMIN]: 'Legacy administrator role; same module permissions as directorate admin in the app',
  [ROLES.ADMIN_DIRECTORATE]: 'Directorate administrator responsible for approvals and oversight',
  [ROLES.ADMIN_SECRETARIAT]: 'Secretariat administrator responsible for approvals and oversight',
  [ROLES.COMMISSIONER]: 'Commissioner-level approver with read/approve responsibilities',
  [ROLES.ADDITIONAL_SECRETARY]: 'Additional Secretary with workflow approval duties',
  [ROLES.JOINT_SECRETARY]: 'Joint Secretary with workflow approval duties',
  [ROLES.DEPUTY_SECRETARY]: 'Deputy Secretary with workflow approval duties',
  [ROLES.UNDER_SECRETARY]: 'Under Secretary with workflow approval duties',
  [ROLES.JOINT_DIRECTOR]: 'Joint Director with branch-level approval duties',
  [ROLES.DEPUTY_DIRECTOR]: 'Deputy Director with branch-level approval duties',
  [ROLES.SUPERINTENDENT]: 'Superintendent-level approver within the Directorate branch',
  [ROLES.ASSISTANT]: 'Assistant-level approver within the Directorate branch',
  [ROLES.SECRETARIAT]: 'Secretariat-level officer involved in approvals and oversight',
  [ROLES.DEO]: 'District-level access to manage schools and teachers within assigned district',
  [ROLES.SDEO]: 'Sub-division level access to manage schools and teachers within assigned sub-division',
  [ROLES.HOI]: 'Institution-level access to manage teachers and school data within assigned institution',
  [ROLES.TEACHER]: 'Limited access to view own profile and medical records'
}

/** Role-specific dashboard guidance for branch approvers and admin officials. */
export const ROLE_DASHBOARD_GUIDANCE: Partial<Record<UserRole, { summary: string; workflow: string }>> = {
  [ROLES.ADMIN_DIRECTORATE]: {
    summary: 'Oversee statewide school and teacher records, finalise Directorate-path transfers, and manage system data.',
    workflow: 'Directorate path: Assistant → Superintendent → Deputy Director → Joint Director → You (Directorate Admin) → Minister',
  },
  [ROLES.ADMIN_SECRETARIAT]: {
    summary: 'Oversee Secretariat-path transfer approvals, joining reports, and statewide oversight for your branch.',
    workflow: 'Secretariat path: Under Secretary → Deputy Secretary → Joint Secretary → Additional Secretary → Commissioner → You (Secretariat Admin)',
  },
  [ROLES.ASSISTANT]: {
    summary: 'First desk in the Directorate chain — verify transfer papers, record remarks, and forward cases upward.',
    workflow: 'You are here: Assistant → Superintendent → Deputy Director → Joint Director → Directorate Admin → Minister',
  },
  [ROLES.SUPERINTENDENT]: {
    summary: 'Validate assistant remarks, check eligibility, and recommend cases to Deputy Director.',
    workflow: 'Assistant → You (Superintendent) → Deputy Director → Joint Director → Directorate Admin → Minister',
  },
  [ROLES.DEPUTY_DIRECTOR]: {
    summary: 'Review superintendent recommendations and advance strong cases to Joint Director.',
    workflow: 'Assistant → Superintendent → You (Deputy Director) → Joint Director → Directorate Admin → Minister',
  },
  [ROLES.JOINT_DIRECTOR]: {
    summary: 'Senior Directorate reviewer — confirm case readiness before Directorate Admin and Minister.',
    workflow: 'Assistant → Superintendent → Deputy Director → You (Joint Director) → Directorate Admin → Minister',
  },
  [ROLES.UNDER_SECRETARY]: {
    summary: 'Early Secretariat-path reviewer for transfers routed through the Secretariat branch.',
    workflow: 'You (Under Secretary) → Deputy Secretary → Joint Secretary → Additional Secretary → Commissioner → Secretariat Admin',
  },
  [ROLES.DEPUTY_SECRETARY]: {
    summary: 'Validate Under Secretary remarks and prepare cases for Joint Secretary review.',
    workflow: 'Under Secretary → You (Deputy Secretary) → Joint Secretary → Additional Secretary → Commissioner → Secretariat Admin',
  },
  [ROLES.JOINT_SECRETARY]: {
    summary: 'Mid-level Secretariat approver — review files and forward with recommendations.',
    workflow: 'Under Secretary → Deputy Secretary → You (Joint Secretary) → Additional Secretary → Commissioner → Secretariat Admin',
  },
  [ROLES.ADDITIONAL_SECRETARY]: {
    summary: 'Senior Secretariat reviewer before Commissioner and Secretariat Admin sign-off.',
    workflow: 'Under Secretary → Deputy Secretary → Joint Secretary → You (Additional Secretary) → Commissioner → Secretariat Admin',
  },
  [ROLES.COMMISSIONER]: {
    summary: 'Commissioner-level sign-off on Secretariat-path transfer cases before final admin action.',
    workflow: 'Under Secretary → Deputy Secretary → Joint Secretary → Additional Secretary → You (Commissioner) → Secretariat Admin',
  },
  [ROLES.SECRETARIAT]: {
    summary: 'Support Secretariat-path approvals, joining reports, and transfer oversight.',
    workflow: 'Secretariat branch approvals route through Under Secretary to Commissioner, then Secretariat Admin.',
  },
  [ROLES.SUPER_ADMIN]: {
    summary: 'Complete system overview — user management, master data, enrollment targets, and statewide oversight.',
    workflow: 'Full access across Directorate and Secretariat paths, user roles, system configuration, and all admin functions.',
  },
  [ROLES.MINISTER]: {
    summary: 'Statewide policy view — transfers, grievances, workforce health, and district-level indicators across Mizoram.',
    workflow: 'Directorate path ends with your approval: Assistant → Superintendent → Deputy Director → Joint Director → Directorate Admin → You (Education Minister).',
  },
}
